import type { Citation } from '@ralph/contracts'
import clsx from 'clsx'
import { AlertTriangle, FileText, Library, X } from 'lucide-react'
import { Fragment, type ReactNode, useMemo, useRef, useState } from 'react'
import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { useFocusTrap } from '@/components/ui/focus-trap.ts'
import { Overlay } from '@/components/ui/Overlay.tsx'
import { Badge, IconButton } from '@/components/ui/primitives.tsx'
import { useI18n } from '@/i18n/index.ts'

/**
 * Renders an answer with its citations wired up.
 *
 * The `[n]` markers the model writes are replaced with interactive chips bound to the
 * passage that was actually retrieved. A marker with no matching citation is left as plain
 * text rather than rendered as a chip: a chip that opens nothing looks like grounding and
 * isn't, which is worse than no chip at all.
 *
 * The chip is set as a small monospace superscript in the citation colour — the same register
 * as every other measured thing in the product — so a reader can tell at a glance which
 * sentences are anchored and which are the model's own connective tissue.
 */

const MARKER = /\[(\d{1,2})\]/g

/**
 * A relevance score is four decimals wherever it appears. Through `Intl` rather than
 * `toFixed` so the separator is the reader's — a Brazilian read "0.8123" here beside
 * "1.234" two rows up, which is the mixed-format bug PRD-27 F-2 names.
 */
const SCORE_DIGITS: Intl.NumberFormatOptions = { minimumFractionDigits: 4, maximumFractionDigits: 4 }

export const Answer = ({
  content,
  citations,
  streaming
}: {
  content: string
  citations: Citation[]
  streaming?: boolean
}) => {
  const [openCitation, setOpenCitation] = useState<Citation | null>(null)
  const byMarker = useMemo(() => new Map(citations.map((c) => [c.marker, c])), [citations])

  /**
   * Markers are substituted at the text-node level so they survive inside lists, tables and
   * emphasis. Pre-processing the raw markdown string instead would break any marker that
   * lands inside a code span or a link.
   */
  const withCitations = (children: ReactNode): ReactNode => {
    const walk = (node: ReactNode): ReactNode => {
      if (typeof node === 'string') {
        if (!MARKER.test(node)) return node
        MARKER.lastIndex = 0

        const parts: ReactNode[] = []
        let cursor = 0
        for (const match of node.matchAll(MARKER)) {
          const index = match.index ?? 0
          const citation = byMarker.get(Number(match[1]))
          if (index > cursor) parts.push(node.slice(cursor, index))
          parts.push(
            citation ? (
              <CitationChip key={`${index}-${match[1]}`} citation={citation} onOpen={() => setOpenCitation(citation)} />
            ) : (
              match[0]
            )
          )
          cursor = index + match[0].length
        }
        if (cursor < node.length) parts.push(node.slice(cursor))
        // biome-ignore lint/suspicious/noArrayIndexKey: positional split of one string; order is the identity
        return parts.map((part, i) => <Fragment key={`p${i}`}>{part}</Fragment>)
      }

      if (Array.isArray(node))
        // biome-ignore lint/suspicious/noArrayIndexKey: markdown children are positional and never reordered
        return node.map((child, i) => <Fragment key={`c${i}`}>{walk(child)}</Fragment>)
      return node
    }

    return walk(children)
  }

  return (
    <>
      <div className={clsx('prose-answer', streaming && 'caret')}>
        <Markdown
          remarkPlugins={[remarkGfm]}
          components={{
            p: ({ children }) => <p>{withCitations(children)}</p>,
            li: ({ children }) => <li>{withCitations(children)}</li>,
            td: ({ children }) => <td>{withCitations(children)}</td>,
            strong: ({ children }) => <strong>{withCitations(children)}</strong>,
            // Untrusted content: an answer can quote a document, and a document can contain
            // a link. Forcing noopener/noreferrer and a new tab keeps a quoted link from
            // navigating the workspace away or reaching `window.opener`.
            a: ({ href, children }) => (
              <a href={href} target="_blank" rel="noopener noreferrer nofollow">
                {children}
              </a>
            )
          }}
        >
          {content}
        </Markdown>
      </div>

      {citations.length > 0 && <SourceList citations={citations} onOpen={setOpenCitation} />}
      {openCitation && <SourceDrawer citation={openCitation} onClose={() => setOpenCitation(null)} />}
    </>
  )
}

const CitationChip = ({ citation, onOpen }: { citation: Citation; onOpen: () => void }) => {
  // Read inside the body, never captured at module scope: a capture taken at import freezes
  // the locale and then renders the previous language with no error at all (PRD-27 F-3).
  const t = useI18n().t.chat

  return (
    <button
      type="button"
      onClick={onOpen}
      title={t.citationTitle(citation.documentTitle, citation.locator, citation.supported)}
      className={clsx(
        'readout ml-px inline-flex items-center rounded-[6px] border px-[5px] py-px align-super text-[10px] leading-[1.3]',
        'transition-[transform,background-color,color] duration-150 hover:-translate-y-px',
        citation.supported
          ? 'border-grounded-soft bg-grounded-soft text-grounded hover:bg-grounded hover:text-[var(--on-grounded)]'
          : // Dashed, not hidden, and not faint either. A citation whose span could not be shown
            // to support the surrounding claim is a signal the reader deserves — which means the
            // number has to stay as readable as a verified one. Hollow-and-pale made the chips
            // read as a rendering fault rather than as a stated caveat.
            'border-dashed border-rule-strong bg-card text-fg-muted hover:border-fg-muted hover:text-fg'
      )}
    >
      {citation.marker}
    </button>
  )
}

/**
 * The row of sources under an answer.
 *
 * Pills rather than a list: at this size the row reads as one gesture — "these are the things
 * it read" — and stays on one or two lines under a 704px column, where a stacked list pushed
 * the provenance panel off the fold on almost every answer.
 */
const SourceList = ({ citations, onOpen }: { citations: Citation[]; onOpen: (c: Citation) => void }) => {
  // Inside the body, never at module scope (PRD-27 F-3, LOC-012).
  const t = useI18n().t.chat

  return (
    <div className="mt-4 flex flex-wrap items-center gap-1.5">
      <span className="eyebrow mr-0.5 inline-flex items-center gap-1.5">
        <Library size={10} aria-hidden />
        {t.sources}
      </span>
      {citations.map((citation) => (
        <button
          key={citation.id}
          type="button"
          onClick={() => onOpen(citation)}
          title={t.sourceOpen(citation.documentTitle, citation.locator)}
          className={clsx(
            'inline-flex h-[27px] min-w-0 max-w-full items-center gap-1.5 rounded-full border border-rule-strong px-2.5',
            'text-[11.5px] font-medium tracking-[-0.01em] text-fg-muted',
            'transition-[transform,color,border-color,background-color] duration-200',
            'hover:-translate-y-px hover:border-grounded hover:bg-grounded-soft hover:text-fg'
          )}
        >
          <FileText size={11} className="shrink-0 text-grounded" aria-hidden />
          <span className="min-w-0 truncate">{citation.documentTitle}</span>
          <span className="readout shrink-0 text-[9.5px] text-fg-subtle">{citation.locator}</span>
          {!citation.supported && <Badge tone="neutral">{t.sourceUnverified}</Badge>}
          {citation.freshness.stale && <Badge tone="primary">{t.sourceStale}</Badge>}
        </button>
      ))}
    </div>
  )
}

/**
 * The passage itself.
 *
 * Trapped, escapable and focus-restoring like every other dialog in the app — it used to be
 * the one `aria-modal` surface with none of the three, so a keyboard reader who opened a
 * citation was left tabbing through the conversation behind it with no way back.
 *
 * The two close controls keep different names on purpose: the browser suite tells the button
 * from the backdrop only by exactness (`'Close'` vs `'Close source detail'`).
 */
const SourceDrawer = ({ citation, onClose }: { citation: Citation; onClose: () => void }) => {
  const panelRef = useRef<HTMLDivElement>(null)
  useFocusTrap(panelRef, { onEscape: onClose })
  // Bound inside the component, never captured at module scope: a module-level capture
  // freezes the locale and then renders the previous language silently (PRD-27 F-3).
  const { formatAgo, formatNumber, t: messages } = useI18n()
  const t = messages.chat

  return (
    <Overlay>
      <div
        className="fade-in fixed inset-0 z-50 flex justify-end"
        role="dialog"
        aria-modal="true"
        aria-label={t.drawerTitle}
      >
        <button
          type="button"
          aria-label={t.drawerCloseBackdrop}
          onClick={onClose}
          className="flex-1 bg-[var(--scrim)]/25 backdrop-blur-[1px]"
        />
        <div
          ref={panelRef}
          tabIndex={-1}
          className="m-3 flex w-[440px] max-w-[92vw] flex-col rounded-panel border border-rule bg-card-solid shadow-frame focus:outline-none"
        >
          <header className="flex items-start justify-between gap-3 border-b border-rule px-4 py-3.5">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <FileText size={14} className="shrink-0 text-grounded" aria-hidden />
                <h2 className="truncate text-[15px]">{citation.documentTitle}</h2>
              </div>
              <p className="readout mt-1 text-fg-subtle">
                {t.drawerMeta(citation.sourceName, citation.locator, formatNumber(citation.score, SCORE_DIGITS))}
              </p>
            </div>
            <IconButton label={t.drawerClose} size={29} onClick={onClose}>
              <X size={14} aria-hidden />
            </IconButton>
          </header>

          {!citation.supported && (
            <div className="flex items-start gap-2 border-b border-rule bg-alert-soft px-4 py-2.5">
              <AlertTriangle size={13} className="mt-0.5 shrink-0 text-alert" aria-hidden />
              <p className="text-[12.5px] leading-relaxed text-fg-muted">{t.drawerUnsupported}</p>
            </div>
          )}

          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3.5">
            <p className="eyebrow mb-2">{t.drawerPassage}</p>
            <p className="whitespace-pre-wrap text-[13.5px] leading-relaxed text-fg-muted">{citation.snippet}</p>
          </div>

          <footer className="border-t border-rule px-4 py-3">
            <p className="readout text-fg-subtle">
              {t.drawerIndexed(formatAgo(citation.freshness.indexedAt), citation.freshness.stale)}
            </p>
          </footer>
        </div>
      </div>
    </Overlay>
  )
}
